import React, { useEffect, useRef, useState } from 'react'
import { useRequest } from "../../hooks/UseRequest"
import Loading from "../../components/Loading"
import { setNotification } from "../../redux/notificationSlice"
import { useDispatch, useSelector } from "react-redux"
import { hasUserPermission } from "../../utils/permissionHelper"
import { selectUser } from "../../redux/userSlice"
import NoRecord from "../../components/elements/NoRecord"
import { FormatDateTimeToSame } from '../../helpers/formatting'

export default function CognitoDashlet({ customerId }) {
  const dispatch = useDispatch()
  const user = useSelector(selectUser)
  const requests = useRequest()
  const requesting = useRef(false)
  const [cognitoDetails, setCognitoDetails] = useState({})
  const [loaded, setLoaded] = useState(false)
  const [tempPassword, setTempPassword] = useState('')

  const getCognitoDetails = async () => {
    requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/cognito/${customerId}`)
    try {
      const response = await requests.get();
      setCognitoDetails(response.data.data)
      setLoaded(true)
    } catch (error) {
      setCognitoDetails({})
      setLoaded(true)
    }
  }

  useEffect(() => {
    if (!requesting.current && Object.keys(cognitoDetails).length === 0) {
      setLoaded(false)
      requesting.current = true
      getCognitoDetails()
    }
  }, [])

  const createCognitoUser = async () => {
    setLoaded(false)
    try {
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/cognito/${customerId}`)
      await requests.post()
      await getCognitoDetails()
      setLoaded(true)
    } catch (err) {
      const message = formatErrorMessage(err)
      dispatch(setNotification({ type: 'error', body: message }))
      setLoaded(true)
    }
  }

  const deleteCognitoUser = async () => {
    setLoaded(false)
    try {
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/cognito/${customerId}`)
      await requests.delete()
      await getCognitoDetails()
      setLoaded(true)
    } catch (err) {
      const message = formatErrorMessage(err)
      dispatch(setNotification({ type: 'error', body: message }))
      setLoaded(true)
    }
  }

  const disableCognitoUser = async (e) => {
    setLoaded(false)
    e.preventDefault()
    try {
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/cognito/${customerId}/disable`)
      await requests.edit()
      await getCognitoDetails()
      setLoaded(true)
    } catch (err) {
      const message = formatErrorMessage(err)
      dispatch(setNotification({ type: 'error', body: message }))
      setLoaded(true)
    }
  }

  const enableCognitoUser = async (e) => {
    setLoaded(false)
    e.preventDefault()
    try {
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/cognito/${customerId}/enable`)
      await requests.edit()
      await getCognitoDetails()
      setLoaded(true)
    } catch (err) {
      const message = formatErrorMessage(err)
      dispatch(setNotification({ type: 'error', body: message }))
      setLoaded(true)
    }
  }

  const resetPassword = async () => {
    setLoaded(false)
    try {
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/cognito/${customerId}/reset_password`)
      const response = await requests.edit()
      setTempPassword(response.data.data.temp_password)
      setLoaded(true)
    } catch (err) {
      const message = formatErrorMessage(err)
      dispatch(setNotification({ type: 'error', body: message }));
      setLoaded(true)
    }
  }

  const sendResetEmail = async () => {
    setLoaded(false)
    try {
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/cognito/${customerId}/reset_password`)
      let data = { send_reset_email: 1 }
      const response = await requests.edit(data)
      setTempPassword(response.data.data.temp_password)
      setLoaded(true)
      if (response?.data?.data?.email_sent) {
        dispatch(setNotification({ type: 'success', body: 'Email sent successfully' }));
      } else {
        dispatch(setNotification({ type: 'error', body: 'Email was not sent' }));
      }
    } catch (err) {
      const message = formatErrorMessage(err)
      dispatch(setNotification({ type: 'error', body: message }));
      setLoaded(true)
    }
  }

  const formatErrorMessage = (error) => {
    let message = 'There was an error submitting'
    if (error?.response?.status === 403) {
      message = error?.response?.data?.message ? error.response.data.message : 'There was an error communicating with the login service'
    } else if (error?.response?.status === 404) {
      message = "The user's account does not exist"
    } else if (error?.response?.status === 400 || error?.response?.status === 422) {
      message = error?.data?.message ? error.data.message : 'There was an error submitting'
    }
    return message
  };

  return (
    <div className="message is-white lox-dashlet-detail">
      <div className="message-header has-background-primary has-text-white lox-dashlet-detail__title py-0">
        <p className="is-size-4">Self-Service Portal Access</p>
      </div>
      <div className="message-body py-0 px-1">
        {loaded && Object.keys(cognitoDetails).length > 0 && (
          <>
            <div className="columns m-0">
              <div className="column py-1 px-0 pr-1">
                <span className="lox-dashlet-detail__item__label has-text-grey is-size-8 pl-2">
                  {tempPassword ? 'Temporary Password' : 'Status'}
                </span>
                <span className="lox-dashlet-detail__item__data is-size-8 pl-2">
                  {/* @TODO: Create display mapping? */}
                  {tempPassword ? tempPassword : cognitoDetails.user_status}
                </span>
                {hasUserPermission(user.data.user_permission_options, 'cognito_update') && (
                  <>
                    <button
                      type="button"
                      className="button has-background-grey has-text-white is-small mr-1 header-button"
                      onClick={resetPassword}
                      style={{ height: "2em", float: "right", marginRight: ".8em !important", width: '7em' }}
                    >
                      Reset Pwd
                    </button>
                    <button
                      type="button"
                      className="button has-background-grey has-text-white is-small mr-1 header-button"
                      onClick={sendResetEmail}
                      style={{ height: "2em", float: "right", marginRight: ".8em !important", width: '7em' }}
                    >
                      Send Reset
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="columns m-0">
              <div className={`column py-1 mb-1 has-text-centered px-0 ${cognitoDetails.enabled ? 'has-background-success' : 'has-background-danger has-text-info-light'} `}>
                <span className="lox-dashlet-detail__item__label is-size-8 ">
                  Acct Enabled
                </span>
              </div>
              <div className={`column ml-1 py-1 mb-1 px-0 ${cognitoDetails.email_verified ? 'has-background-success' : 'has-background-danger has-text-info-light'} `}>
                <div className="lox-dashlet-detail__item py-0 px-1 has-text-centered">
                  <span className="lox-dashlet-detail__item__label is-size-8">
                    Email Verified
                  </span>
                </div>
              </div>
            </div>

            <div className="columns m-0">
              <div className="column pr-1 py-1 px-0">
                <span className="lox-dashlet-detail__item__label has-text-grey is-size-8 pl-2">
                  Last Site Activity
                </span>
                <span className="lox-dashlet-detail__item__data is-size-8 pl-2">
                  {/* backend is not returning last_site_activity with timezone, so its returning as arizona time as default */}
                  {cognitoDetails?.last_site_activity ? FormatDateTimeToSame(cognitoDetails.last_site_activity, 'MM/dd/yyyy hh:mm:ss a') : '-'}
                </span>

                {(hasUserPermission(user.data.user_permission_options, 'cognito_update') || hasUserPermission(user.data.user_permission_options, 'cognito_delete')) && (
                  <>
                    {hasUserPermission(user.data.user_permission_options, 'cognito_update') && (
                      cognitoDetails.enabled ? (
                        <button
                          type="button"
                          className="button has-background-grey has-text-white is-small mr-1 header-button px-1"
                          onClick={disableCognitoUser}
                          style={{ height: '2em', float: 'right', marginRight: '.8em !important', width: '7em' }}
                        >
                          Disable Acct
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="button has-background-grey has-text-white is-small mr-1 header-button px-1"
                          onClick={enableCognitoUser}
                          style={{ height: '2em', float: 'right', marginRight: '.8em !important', width: '7em' }}
                        >
                          Enable Acct
                        </button>
                      )
                    )}

                    {hasUserPermission(user.data.user_permission_options, 'cognito_delete') && (
                      <button
                        type="button"
                        className="button has-background-danger has-text-white is-small mr-1 header-button"
                        onClick={deleteCognitoUser}
                        style={{ height: '2em', float: 'right', marginRight: '.8em !important', width: '7em' }}
                      >
                        Delete Acct
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>

          </>
        )}
        {!loaded && <Loading />}
        {/* eslint-disable-next-line no-nested-ternary */}
        {loaded && Object.keys(cognitoDetails).length === 0
          ? hasUserPermission(user.data.user_permission_options, 'cognito_create')
            ? (
              <div className="is-size-6 is-italic" style={{ textAlign: "center", padding: "20px", color: "grey" }}>
                <button
                  type="submit"
                  className="button is-link is-small ml-5 p-2"
                  onClick={createCognitoUser}
                >
                  Create Cognito User
                </button>
              </div>
            )
            : <NoRecord />
          : ''}
      </div>
    </div>
  )
}
